import {
  CheckIcon,
  ChevronDownIcon,
  WrenchIcon
} from '@heroicons/react/20/solid';
import { Chip, cn, Tooltip } from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';

import Typography from '../Typography';

export interface Timeline {
  stage: string;
  started_at: string | null;
  finished_at: string | null;
  client_notes: string | null;
  estimated_started_at: string;
  estimated_finished_at: string;
  timelineIndex: number;
}

const DEFAULT_EXPECTED_TOOLTIP =
  'Projected date — may adjust depending on real-time progress.';

/** Internal chip shown next to projected dates. */
const ExpectedTag: React.FC<{ popOverText?: string }> = ({
  popOverText = DEFAULT_EXPECTED_TOOLTIP
}) => (
  <Tooltip
    closeDelay={0}
    className="rounded-lg shadow-md font-medium text-base"
    content={popOverText}
  >
    <Chip
      color="default"
      className="bg-white border border-gray-4 rounded-lg !px-[6px] !h-6"
      classNames={{ content: '!p-0' }}
    >
      <Typography variant="body-3" className="text-secondary-text">
        Expected
      </Typography>
    </Chip>
  </Tooltip>
);

interface EntityTimelineProps {
  timeline: Timeline[];
  currentStage: Timeline;
  isOpen: boolean;
  stageIcons: Record<string, JSX.Element>;
  stageLabels: Record<string, string>;
  stageToGroupMap: Record<string, string>;
}

const EntityTimeline: React.FC<EntityTimelineProps> = ({
  timeline,
  currentStage,
  isOpen,
  stageIcons,
  stageLabels,
  stageToGroupMap
}) => {
  const [expandedStage, setExpandedStage] = useState<string[]>([]);

  const dynamicStageGroups = useMemo(() => {
    if (!timeline) return [];

    const groups: {
      key: string;
      title: string;
      icon: JSX.Element;
      isCompleted: boolean;
      groupIndex: number;

      stages: {
        stage: string;
        label: string;
        finished_at?: string | null;
        estimated_finished_at?: string | null;
        timelineIndex: number;
        started_at?: string | null;
        estimated_started_at?: string | null;
      }[];
    }[] = [];

    let currentGroupKey = '';
    let currentGroupStages: {
      stage: string;
      label: string;
      finished_at?: string | null;
      estimated_finished_at?: string | null;
      timelineIndex: number;
    }[] = [];
    let groupIndex = 0;

    timeline.forEach((entry, index) => {
      // this is how we determine the grouping
      const groupTitle =
        stageToGroupMap[entry.stage] ||
        (index > 0 ? stageToGroupMap[timeline[index - 1].stage] : undefined) ||
        `Group ${index + 1}`;

      const label = stageLabels[entry.stage] ?? entry.stage;

      // if group title is different from the current group key and there are stages in the current group, push the current group to the groups array
      // if the group title is unknown, we use the group before it
      // fall back if the group with index
      if (groupTitle !== currentGroupKey && currentGroupStages.length) {
        const isGroupCompleted = currentGroupStages.every(
          (stage) => stage.finished_at
        );

        const icon = isGroupCompleted ? (
          <CheckIcon className="w-5 h-5" />
        ) : (
          stageIcons[currentGroupKey] || <WrenchIcon className="w-5 h-5" />
        );

        groups.push({
          key: `${currentGroupKey.toLowerCase()}-${groupIndex}`,
          title: currentGroupKey,
          icon,
          stages: currentGroupStages,
          isCompleted: isGroupCompleted,
          groupIndex: groupIndex
        });

        // reset the current group stages and increment the group index
        currentGroupStages = [];
        groupIndex++;
      }

      // set the current group key to the group title and add the entry to the current group stages
      currentGroupKey = groupTitle;
      currentGroupStages.push({ ...entry, label, timelineIndex: index });
    });

    if (currentGroupStages.length) {
      const isGroupCompleted = currentGroupStages.every(
        (stage) => stage.finished_at
      );

      const icon = isGroupCompleted ? (
        <CheckIcon className="w-5 h-5" />
      ) : (
        stageIcons[currentGroupKey] || <WrenchIcon className="w-5 h-5" />
      );

      groups.push({
        key: `${currentGroupKey.toLowerCase()}-${groupIndex}`,
        title: currentGroupKey,
        icon,
        stages: currentGroupStages,
        isCompleted: isGroupCompleted,
        groupIndex: groupIndex
      });
    }

    return groups;
  }, [timeline, stageIcons, stageLabels, stageToGroupMap]);

  useEffect(() => {
    if (isOpen && timeline) {
      const groupToExpand = dynamicStageGroups.find((group) => {
        const groupStages = group.stages.map((s) => s.timelineIndex);
        const currentIndex = currentStage?.timelineIndex ?? -1;

        return groupStages.includes(currentIndex);
      })?.key;

      if (groupToExpand) {
        setExpandedStage([groupToExpand]);
      }
    }
  }, [isOpen, timeline, currentStage?.timelineIndex, dynamicStageGroups]);

  return (
    <div className="flex flex-col">
      {dynamicStageGroups.map((group, index) => {
        const lastStageData = group?.stages[group.stages.length - 1];
        const isCurrentStageInGroup = group.stages.some(
          (stage) => stage.timelineIndex === currentStage?.timelineIndex
        );

        return (
          <div
            key={group.key}
            className="flex flex-col cursor-pointer"
            onClick={() =>
              setExpandedStage((prev) =>
                prev.includes(group.key)
                  ? prev.filter((key) => key !== group.key)
                  : [...prev, group.key]
              )
            }
          >
            <div className="grid grid-cols-[20px_44px_1fr] items-center gap-3">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{
                  rotate: expandedStage.includes(group.key) ? 180 : 0
                }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon className="w-5 h-5" />
              </motion.div>
              <div
                className={cn(
                  'text-white flex items-center justify-center w-11 h-11 bg-primary border-2 border-white rounded-full',
                  !group.isCompleted && !isCurrentStageInGroup && 'bg-gray-5'
                )}
              >
                {group.icon}
              </div>
              <div className="flex flex-col">
                <Typography
                  variant="h6"
                  weight="bold"
                  className={cn(
                    !group.isCompleted &&
                      !isCurrentStageInGroup &&
                      'text-secondary-text'
                  )}
                >
                  {group.title}
                </Typography>
                {!expandedStage.includes(group.key) && (
                  <>
                    {lastStageData?.finished_at ? (
                      <Typography variant="body-2" weight="medium">
                        Completed:{' '}
                        {format(lastStageData.finished_at, 'MMM d, yyyy')}
                      </Typography>
                    ) : (
                      lastStageData?.estimated_finished_at && (
                        <div className="flex items-center gap-1">
                          <Typography
                            variant="body-2"
                            weight="medium"
                            className={cn(
                              !group.isCompleted &&
                                !isCurrentStageInGroup &&
                                'text-secondary-text'
                            )}
                          >
                            Completion:{' '}
                            {format(
                              lastStageData.estimated_finished_at,
                              'MMM d, yyyy'
                            )}
                          </Typography>
                          <ExpectedTag />
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>

            <AnimatePresence initial={false}>
              {index < dynamicStageGroups.length - 1 &&
                !expandedStage.includes(group.key) && (
                  <motion.div
                    key={`${group.key}-connector`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 28, opacity: 1 }}
                    exit={{
                      height: 0,
                      opacity: 0,
                      transition: { delay: 0.05 }
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="grid grid-cols-[20px_44px_1fr] items-center gap-3 overflow-hidden"
                  >
                    <div />
                    <div className="flex items-center justify-center relative flex-col">
                      <div
                        className={cn(
                          'w-[2px] min-h-[28px] bg-primary',
                          !group.isCompleted && 'bg-gray-5'
                        )}
                        style={{ height: '100%' }}
                      />
                    </div>
                    <div />
                  </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {expandedStage.includes(group.key) && (
                <motion.div
                  key="content"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { height: 'auto', opacity: 1 },
                    collapsed: { height: 0, opacity: 0 }
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-col my-1">
                    {group.stages.map((groupStage, index) => {
                      const started = groupStage.started_at;
                      const expectedStarted = groupStage.estimated_started_at;

                      const isCurrentStage =
                        groupStage.timelineIndex ===
                        currentStage?.timelineIndex;

                      const isLaterStage =
                        groupStage.timelineIndex >
                        (currentStage?.timelineIndex ?? -1);

                      const isLastStage =
                        groupStage?.timelineIndex ===
                        (timeline?.length ?? 0) - 1;

                      const isStartIndex = index === 0;
                      const isEndIndex = index === group.stages.length - 1;

                      return (
                        <div
                          className="grid grid-cols-[20px_44px_1fr] items-center gap-3"
                          key={groupStage.stage}
                        >
                          <div />
                          <div className="flex items-center justify-center relative flex-col">
                            <div
                              className={cn(
                                'w-[2px] h-[28px] bg-primary',
                                isLaterStage && 'bg-gray-5',
                                isStartIndex && 'h-[36px]'
                              )}
                            />
                            <motion.div
                              className={cn(
                                'size-2 rounded-full z-10 relative bg-primary',
                                isLaterStage && 'bg-gray-5',
                                groupStage.stage === 'rejected' && 'bg-red-8'
                              )}
                              animate={
                                isCurrentStage
                                  ? {
                                      boxShadow:
                                        groupStage.stage === 'rejected'
                                          ? [
                                              '0 0 0px 0px rgba(224, 49, 49, 0.5)',
                                              '0 0 0px 15px rgba(224, 49, 49, 0)'
                                            ]
                                          : [
                                              '0 0 0px 0px rgba(0, 0, 238, 0.5)',
                                              '0 0 0px 15px rgba(0, 0, 238, 0)'
                                            ]
                                    }
                                  : {}
                              }
                              transition={{
                                duration: 1,
                                repeat: Infinity,
                                repeatType: 'loop',
                                ease: 'easeOut',
                                repeatDelay: 0.8
                              }}
                            />

                            <div
                              className={cn(
                                'w-[2px] h-[28px] bg-primary',
                                isLaterStage && 'bg-gray-5',
                                isCurrentStage && 'bg-gray-5',
                                isLastStage && 'bg-transparent',
                                isEndIndex && 'h-[36px]'
                              )}
                            />
                          </div>
                          <motion.div
                            className={cn(
                              'flex flex-col',
                              isEndIndex && 'relative top-[-6px]'
                            )}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{
                              duration: 0.3,
                              ease: 'easeInOut',
                              delay: index * 0.1
                            }}
                          >
                            <Typography
                              variant="h7"
                              weight="medium"
                              className={cn(
                                isLaterStage && 'text-secondary-text'
                              )}
                            >
                              {groupStage.label}
                            </Typography>
                            <div className="flex items-center gap-1">
                              {started ? (
                                <Typography
                                  variant="body-2"
                                  weight="regular"
                                  className={cn(
                                    isLaterStage && 'text-secondary-text'
                                  )}
                                >
                                  {format(started, 'MMM d, yyyy')}
                                </Typography>
                              ) : (
                                expectedStarted && (
                                  <>
                                    <Typography
                                      variant="body-2"
                                      weight="regular"
                                      className={cn(
                                        isLaterStage && 'text-secondary-text'
                                      )}
                                    >
                                      {format(expectedStarted, 'MMM d, yyyy')}
                                    </Typography>
                                    {groupStage.stage !== 'rejected' &&
                                      !isCurrentStage && <ExpectedTag />}
                                  </>
                                )
                              )}
                              {groupStage.stage === 'rejected' && (
                                <Chip
                                  color="default"
                                  className="bg-red-0 border border-transparent rounded-lg !px-[6px] !h-6"
                                  classNames={{ content: '!p-0' }}
                                >
                                  <Typography
                                    variant="body-3"
                                    className="text-red-9"
                                  >
                                    Rejected
                                  </Typography>
                                </Chip>
                              )}
                            </div>
                          </motion.div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default EntityTimeline;
